import { ApiError } from '../identity/types';
import { PAYSLIP_SORTS, type PayslipSort } from './payslipList';
import type { ReportsGateway } from './ReportsGateway';
import type {
  CashRequirementResponse, EmployeePaySummaryResponse, EmployeePayslipListQuery, EmployeePayslipListResponse, EmployeePayslipResponse,
  PayrollRegisterResponse, PayrollRunSummary,
} from './types';

const MOCK_RUN: PayrollRunSummary = {
  id: 'mock-payroll-run-1',
  status: 'Approved',
  checkDate: '2026-03-16',
  periodStartDate: '2026-03-01',
  periodEndDate: '2026-03-14',
};

const MOCK_LINE = {
  employeeId: 'mock-employee-1',
  employeeNumber: 'EX-0001',
  employeeName: 'Morgan Example',
  payrollCalculationId: 'mock-calc-1',
  grossEarnings: 1000,
  employeeTaxes: 100,
  employerTaxes: 62,
  preTaxDeductionsTotal: 50,
  postTaxDeductionsTotal: 20,
  garnishmentsTotal: 30,
  netPay: 800,
};

// EPN-76: a second, older run with its own distinct lines and totals so run selection is observable.
const MOCK_RUN_2: PayrollRunSummary = {
  id: 'mock-payroll-run-2',
  status: 'Approved',
  checkDate: '2026-03-02',
  periodStartDate: '2026-02-15',
  periodEndDate: '2026-02-28',
};

const MOCK_RUN_2_LINES = [
  { ...MOCK_LINE, employeeId: 'mock-employee-2', employeeNumber: 'EX-0002', employeeName: 'Riley Sample', payrollCalculationId: 'mock-calc-2',
    grossEarnings: 2000, employeeTaxes: 250, employerTaxes: 140, preTaxDeductionsTotal: 100, postTaxDeductionsTotal: 40, garnishmentsTotal: 10, netPay: 1600 },
  { ...MOCK_LINE, employeeId: 'mock-employee-3', employeeNumber: 'EX-0003', employeeName: 'Avery Placeholder', payrollCalculationId: 'mock-calc-3',
    grossEarnings: 500, employeeTaxes: 40, employerTaxes: 31, preTaxDeductionsTotal: 0, postTaxDeductionsTotal: 10, garnishmentsTotal: 0, netPay: 450 },
];

// EPN-236 / EPN-237: the extra runs below exist so every derived payslip status can be unit-tested, screenshotted and
// Playwright-exercised. They are returned ONLY by the opt-in `epn-mock-payslips` scenarios below, so every other spec
// keeps its deterministic two-run data (3 Approved payslips).
const MOCK_PAID_RUN: PayrollRunSummary = { id: 'mock-payroll-run-paid', status: 'Approved', checkDate: '2026-02-16', periodStartDate: '2026-02-01', periodEndDate: '2026-02-14' };
const MOCK_VOIDED_RUN: PayrollRunSummary = { id: 'mock-payroll-run-voided', status: 'Approved', checkDate: '2026-02-02', periodStartDate: '2026-01-18', periodEndDate: '2026-01-31' };
const MOCK_PROCESSING_RUN: PayrollRunSummary = { id: 'mock-payroll-run-processing', status: 'Approved', checkDate: '2026-03-30', periodStartDate: '2026-03-15', periodEndDate: '2026-03-28' };
const MOCK_FAILED_RUN: PayrollRunSummary = { id: 'mock-payroll-run-failed', status: 'Approved', checkDate: '2026-04-13', periodStartDate: '2026-03-29', periodEndDate: '2026-04-11' };

const MOCK_PAID_LINES = [
  { ...MOCK_LINE, employeeId: 'mock-employee-4', employeeNumber: 'EX-0004', employeeName: 'Jordan Fixture', payrollCalculationId: 'mock-calc-4',
    grossEarnings: 3200, employeeTaxes: 480, employerTaxes: 220, preTaxDeductionsTotal: 160, postTaxDeductionsTotal: 40, garnishmentsTotal: 0, netPay: 2520 },
];
const MOCK_VOIDED_LINES = [
  { ...MOCK_LINE, employeeId: 'mock-employee-5', employeeNumber: 'EX-0005', employeeName: 'Casey Sandbox', payrollCalculationId: 'mock-calc-5',
    grossEarnings: 2800, employeeTaxes: 400, employerTaxes: 190, preTaxDeductionsTotal: 100, postTaxDeductionsTotal: 0, garnishmentsTotal: 0, netPay: 2300 },
];
const MOCK_PROCESSING_LINES = [
  { ...MOCK_LINE, employeeId: 'mock-employee-6', employeeNumber: 'EX-0006', employeeName: 'Sam Testdata', payrollCalculationId: 'mock-calc-6',
    grossEarnings: 1500, employeeTaxes: 180, employerTaxes: 100, preTaxDeductionsTotal: 0, postTaxDeductionsTotal: 20, garnishmentsTotal: 0, netPay: 1300 },
];
const MOCK_FAILED_LINES = [
  { ...MOCK_LINE, employeeId: 'mock-employee-7', employeeNumber: 'EX-0007', employeeName: 'Taylor Retry', payrollCalculationId: 'mock-calc-7',
    grossEarnings: 1800, employeeTaxes: 220, employerTaxes: 120, preTaxDeductionsTotal: 0, postTaxDeductionsTotal: 0, garnishmentsTotal: 0, netPay: 1580 },
];

type MockLine = typeof MOCK_RUN_2_LINES[number];

/** What the server would derive for one employee on one run. `statusSince` is a real UTC instant (ISO-8601 with a trailing Z), like the server's. */
interface MockFacts { status: string; statusSince: string | null; payMethod: string | null; simulated: boolean; paymentFailed: boolean; memo: string | null }

interface MockPayslip { run: PayrollRunSummary; line: MockLine; facts: MockFacts }

const approvedFacts = (run: PayrollRunSummary, payMethod: string | null = null): MockFacts =>
  ({ status: 'Approved', statusSince: `${run.checkDate}T16:05:00Z`, payMethod, simulated: false, paymentFailed: false, memo: null });

function basePayslips(withMethods: boolean): MockPayslip[] {
  return [
    { run: MOCK_RUN, line: MOCK_LINE, facts: approvedFacts(MOCK_RUN, withMethods ? 'Check' : null) },
    ...MOCK_RUN_2_LINES.map((line) => ({ run: MOCK_RUN_2, line, facts: approvedFacts(MOCK_RUN_2) })),
  ];
}

/** Rows for the opt-in "mixed" / "simulated" scenarios. `simulatedPaid` is what the real staging backend reports for a settled mock payment. */
function mixedPayslips(simulatedPaid: boolean, withFailed: boolean): MockPayslip[] {
  const rows: MockPayslip[] = [
    ...basePayslips(true),
    { run: MOCK_PROCESSING_RUN, line: MOCK_PROCESSING_LINES[0] as MockLine,
      facts: { status: 'Processing', statusSince: '2026-03-30T18:20:00Z', payMethod: 'DirectDeposit', simulated: simulatedPaid, paymentFailed: false, memo: null } },
    { run: MOCK_PAID_RUN, line: MOCK_PAID_LINES[0] as MockLine,
      facts: { status: 'Paid', statusSince: '2026-02-16T23:42:00Z', payMethod: 'DirectDeposit', simulated: simulatedPaid, paymentFailed: false, memo: 'Synthetic fixture memo' } },
    { run: MOCK_VOIDED_RUN, line: MOCK_VOIDED_LINES[0] as MockLine,
      facts: { status: 'Voided', statusSince: '2026-02-09T17:30:00Z', payMethod: 'DirectDeposit', simulated: false, paymentFailed: false, memo: null } },
  ];
  if (withFailed) {
    rows.push({ run: MOCK_FAILED_RUN, line: MOCK_FAILED_LINES[0] as MockLine,
      facts: { status: 'Approved', statusSince: '2026-04-13T16:05:00Z', payMethod: 'DirectDeposit', simulated: false, paymentFailed: true, memo: null } });
  }
  return rows;
}

/** Scenario flag for the Employee Payslips screens, read from localStorage on every call so a Playwright test (or a
 * developer) can switch it without a rebuild:
 *  - "mixed": Approved, Processing, Paid, Voided with Pay Method values (Paid is NOT simulated: the generic contract);
 *  - "simulated": the same plus a failed-payment payslip, with Paid reported as simulated, as the real staging backend does;
 *  - "degraded": the caller may not read payment facts or reversals, so every payslip is Approved and a notice shows;
 *  - "empty": no payslips; "error": the list request fails.
 * Anything else (the default) is the original deterministic two-run data: 3 Approved payslips, no pay method. */
export const MOCK_PAYSLIP_SCENARIO_KEY = 'epn-mock-payslips';
function payslipScenario(): string | null {
  try {
    return window.localStorage.getItem(MOCK_PAYSLIP_SCENARIO_KEY);
  } catch {
    return null;
  }
}
function activePayslips(): MockPayslip[] {
  switch (payslipScenario()) {
    case 'empty': return [];
    case 'mixed': return mixedPayslips(false, false);
    case 'simulated': return mixedPayslips(true, true);
    default: return basePayslips(false);
  }
}

/** Mirrors the server's total order (EPN-239): pay date or name, either direction, ties broken the same way every time. An unknown `sort` is a 400, like the server's. */
function sorter(sort: string | undefined): (a: MockPayslip, b: MockPayslip) => number {
  const key = sort ?? 'checkDate:desc';
  if (!PAYSLIP_SORTS.includes(key as PayslipSort)) throw new ApiError(400, `'sort' must be one of ${PAYSLIP_SORTS.join(', ')}.`);
  const byName = (a: MockPayslip, b: MockPayslip) => a.line.employeeName.toLowerCase().localeCompare(b.line.employeeName.toLowerCase());
  const byDateDesc = (a: MockPayslip, b: MockPayslip) => b.run.checkDate.localeCompare(a.run.checkDate);
  const tail = (a: MockPayslip, b: MockPayslip) => a.line.employeeNumber.localeCompare(b.line.employeeNumber) || a.run.id.localeCompare(b.run.id);
  switch (key) {
    case 'employeeName:asc': return (a, b) => byName(a, b) || byDateDesc(a, b) || tail(a, b);
    case 'employeeName:desc': return (a, b) => byName(b, a) || byDateDesc(a, b) || tail(a, b);
    // Pay-date ties: the mock orders by name (the real server keeps the run's own attachment order); both are deterministic.
    case 'checkDate:asc': return (a, b) => byDateDesc(b, a) || byName(a, b) || tail(a, b);
    default: return (a, b) => byDateDesc(a, b) || byName(a, b) || tail(a, b);
  }
}

function buildListResponse(query: EmployeePayslipListQuery): EmployeePayslipListResponse {
  const scenario = payslipScenario();
  const all = activePayslips();
  const degraded = scenario === 'degraded';
  const year = new Date().getFullYear();
  const search = query.search?.trim().toLowerCase();
  const matches = all
    .filter(({ run, line, facts }) => {
      if (query.payDateFrom && run.checkDate < query.payDateFrom) return false;
      if (query.payDateTo && run.checkDate > query.payDateTo) return false;
      if (query.status && facts.status !== query.status) return false;
      if (query.payMethod && facts.payMethod !== query.payMethod) return false;
      if (query.payrollRunId && run.id !== query.payrollRunId) return false;
      if (query.employeeId && line.employeeId !== query.employeeId) return false;
      if (search && !line.employeeName.toLowerCase().includes(search) && !line.employeeNumber.toLowerCase().includes(search)) return false;
      return true;
    });
  matches.sort(sorter(query.sort));
  const start = (query.page - 1) * query.pageSize;
  const runs = [...new Map(all.map((p) => [p.run.id, p.run])).values()].sort((a, b) => b.checkDate.localeCompare(a.checkDate));
  const last = runs[0];
  return {
    items: matches.slice(start, start + query.pageSize).map(({ run, line, facts }) => ({
      payrollRunId: run.id, employeeId: line.employeeId, employeeNumber: line.employeeNumber, employeeName: line.employeeName,
      periodStartDate: run.periodStartDate, periodEndDate: run.periodEndDate, checkDate: run.checkDate,
      grossPay: line.grossEarnings, netPay: line.netPay,
      status: degraded ? 'Approved' : facts.status, statusSince: degraded ? `${run.checkDate}T16:05:00Z` : facts.statusSince,
      payMethod: degraded ? null : facts.payMethod, simulated: degraded ? false : facts.simulated, paymentFailed: degraded ? false : facts.paymentFailed,
    })),
    page: query.page,
    pageSize: query.pageSize,
    total: matches.length,
    summary: {
      totalNetPayYtd: all.filter((p) => p.facts.status !== 'Voided' && p.run.checkDate.startsWith(`${year}-`)).reduce((sum, p) => sum + p.line.netPay, 0),
      ytdYear: year,
      employeesWithPayslips: new Set(all.map((p) => p.line.employeeId)).size,
      lastPeriod: last ? { start: last.periodStartDate, end: last.periodEndDate, checkDate: last.checkDate } : null,
      approvedCount: all.filter((p) => degraded || p.facts.status === 'Approved').length,
    },
    payPeriods: runs.map((run) => ({ payrollRunId: run.id, periodStartDate: run.periodStartDate, periodEndDate: run.periodEndDate, checkDate: run.checkDate })),
    paymentStatusAvailable: !degraded,
    reversalsAvailable: !degraded,
    runsTruncated: false,
  };
}

/** Local-development-only in-memory stand-in — the production build (VITE_USE_MOCKS=false, the default
 * in .env) always uses HttpReportsGateway against the real EPN.Reports.Api. */
export class MockReportsGateway implements ReportsGateway {
  // The real gateway returns only Approved runs. The payslip scenarios no longer affect this method (the Employee Payslips list
  // uses `listEmployeePayslips`), so the Payroll Register / Details screens keep their two deterministic runs.
  async listApprovedPayrollRuns(): Promise<PayrollRunSummary[]> {
    return [MOCK_RUN, MOCK_RUN_2];
  }

  async listEmployeePayslips(query: EmployeePayslipListQuery): Promise<EmployeePayslipListResponse> {
    if (payslipScenario() === 'error') throw new ApiError(500, 'Server error');
    return buildListResponse(query);
  }

  async getPayrollRegister(payrollRunId: string): Promise<PayrollRegisterResponse> {
    const run = [MOCK_RUN, MOCK_RUN_2].find((r) => r.id === payrollRunId) ?? MOCK_RUN;
    const lines = run.id === MOCK_RUN_2.id ? MOCK_RUN_2_LINES : [MOCK_LINE];
    const sum = (pick: (l: typeof MOCK_LINE) => number) => lines.reduce((total, l) => total + pick(l), 0);
    return {
      payrollRunId, periodStartDate: run.periodStartDate, periodEndDate: run.periodEndDate,
      checkDate: run.checkDate, status: run.status,
      totals: {
        grossEarnings: sum((l) => l.grossEarnings), employeeTaxes: sum((l) => l.employeeTaxes), employerTaxes: sum((l) => l.employerTaxes),
        preTaxDeductionsTotal: sum((l) => l.preTaxDeductionsTotal), postTaxDeductionsTotal: sum((l) => l.postTaxDeductionsTotal),
        garnishmentsTotal: sum((l) => l.garnishmentsTotal), netPay: sum((l) => l.netPay),
      },
      lines,
    };
  }

  async getCashRequirement(payrollRunId: string): Promise<CashRequirementResponse> {
    return {
      payrollRunId, checkDate: MOCK_RUN.checkDate, status: 'Approved',
      totalGrossEarnings: MOCK_LINE.grossEarnings, totalEmployerTaxes: MOCK_LINE.employerTaxes,
      totalCashRequired: MOCK_LINE.grossEarnings + MOCK_LINE.employerTaxes,
    };
  }

  async getEmployeePaySummary(employeeId: string, from: string, to: string): Promise<EmployeePaySummaryResponse> {
    return {
      employeeId, employeeNumber: MOCK_LINE.employeeNumber, employeeName: MOCK_LINE.employeeName, from, to,
      totals: {
        grossEarnings: MOCK_LINE.grossEarnings, employeeTaxes: MOCK_LINE.employeeTaxes, employerTaxes: MOCK_LINE.employerTaxes,
        preTaxDeductionsTotal: MOCK_LINE.preTaxDeductionsTotal, postTaxDeductionsTotal: MOCK_LINE.postTaxDeductionsTotal,
        garnishmentsTotal: MOCK_LINE.garnishmentsTotal, netPay: MOCK_LINE.netPay,
      },
      lines: [{
        payrollRunId: MOCK_RUN.id, checkDate: MOCK_RUN.checkDate, grossEarnings: MOCK_LINE.grossEarnings,
        employeeTaxes: MOCK_LINE.employeeTaxes, employerTaxes: MOCK_LINE.employerTaxes,
        preTaxDeductionsTotal: MOCK_LINE.preTaxDeductionsTotal, postTaxDeductionsTotal: MOCK_LINE.postTaxDeductionsTotal,
        garnishmentsTotal: MOCK_LINE.garnishmentsTotal, netPay: MOCK_LINE.netPay,
      }],
    };
  }

  async getEmployeePayslip(payrollRunId: string, employeeId: string): Promise<EmployeePayslipResponse> {
    // Mirrors the real endpoint's refusal contract: an unknown run, or an employee who is not part of that
    // run, is a bare 404 — never a silently substituted payslip.
    // Every run behind a payslip is Approved; the derived status comes from the scenario (see above).
    const found = activePayslips().find((p) => p.run.id === payrollRunId && p.line.employeeId === employeeId);
    if (!found) throw new ApiError(404, 'Not Found');
    const { run, line } = found;
    const degraded = payslipScenario() === 'degraded';
    const facts = degraded ? approvedFacts(run) : found.facts;

    return {
      payrollRunId, employeeId, employeeNumber: line.employeeNumber, employeeName: line.employeeName,
      ssnLast4: '6789', companyName: 'Acme Payroll Services (mock)',
      periodStartDate: run.periodStartDate, periodEndDate: run.periodEndDate, checkDate: run.checkDate, status: facts.status,
      statusSince: facts.statusSince, payMethod: facts.payMethod, simulated: facts.simulated, paymentFailed: facts.paymentFailed,
      paymentStatusAvailable: !degraded, reversalsAvailable: !degraded, memo: facts.memo,
      grossEarnings: line.grossEarnings,
      employeeTaxLines: [
        { description: 'Federal Income Tax', amount: line.employeeTaxes * 0.7 },
        { description: 'Social Security (Employee)', amount: line.employeeTaxes * 0.3 },
      ],
      employerTaxLines: [
        { description: 'Social Security (Employer Match)', amount: line.employerTaxes * 0.8 },
        { description: 'FUTA', amount: line.employerTaxes * 0.2 },
      ],
      employeeTaxesTotal: line.employeeTaxes, employerTaxesTotal: line.employerTaxes,
      preTaxDeductionsTotal: line.preTaxDeductionsTotal, postTaxDeductionsTotal: line.postTaxDeductionsTotal,
      garnishmentsTotal: line.garnishmentsTotal, netPay: line.netPay,
      leave: [
        { leavePlanName: 'Vacation', used: 0, accrued: 15, available: 45 },
        { leavePlanName: 'Sick Leave', used: 0, accrued: 5.78, available: 29.78 },
      ],
      // Year to date = this payslip plus one earlier period (mock arithmetic: an identical one). A voided payslip is left OUT of its own year to date
      // (EPN-239), so only the earlier period remains (mock: half of this payslip's figures); the "Current" figures above stay the payslip's own.
      ytd: (() => {
        const periods = facts.status === 'Voided' ? 0.5 : 2;
        return {
          grossEarnings: line.grossEarnings * periods, employeeTaxes: line.employeeTaxes * periods, employerTaxes: line.employerTaxes * periods,
          preTaxDeductionsTotal: line.preTaxDeductionsTotal * periods, postTaxDeductionsTotal: line.postTaxDeductionsTotal * periods,
          garnishmentsTotal: line.garnishmentsTotal * periods, netPay: line.netPay * periods,
        };
      })(),
    };
  }
}
